import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, DollarSign, Edit, Save, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { backendService } from '../lib/api';

const PropertyItems = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);

  useEffect(() => {
    if (propertyId) {
      fetchPropertyDetails();
      fetchAvailableItems();
    }
  }, [propertyId]);

  const fetchPropertyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await backendService.request(`/quote-properties/${propertyId}`, { method: 'GET' });
      setProperty(response.property);
      setEditItems(response.property.quote_items || []);
    } catch (err) {
      setError('Failed to load property details');
      console.error('Error fetching property details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const response = await backendService.request('/items', { method: 'GET' });
      setAvailableItems(response.items || []);
    } catch (err) {
      console.error('Error fetching available items:', err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await backendService.request(`/quote-properties/${propertyId}/items`, {
        method: 'PUT',
        body: JSON.stringify({ items: editItems })
      });
      
      // Refresh property details
      await fetchPropertyDetails();
      setIsEditing(false);
      alert('Property items updated successfully!');
    } catch (err) {
      alert('Failed to update property items');
      console.error('Error updating property items:', err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditItems(property?.quote_items || []);
  };

  const addItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      item_id: '',
      quantity: 1,
      unit_rental_price: 0,
      total_rental_price: 0,
      unit_laundering_price: 0,
      total_laundering_price: 0,
      isNew: true
    };
    setEditItems(prev => [...prev, newItem]);
  };

  const removeItem = (itemId) => {
    setEditItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
    setEditItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // Recalculate totals if quantity or unit prices change
        if (field === 'quantity' || field === 'unit_rental_price') {
          updatedItem.total_rental_price = (updatedItem.quantity || 0) * (updatedItem.unit_rental_price || 0);
        }
        if (field === 'quantity' || field === 'unit_laundering_price') {
          updatedItem.total_laundering_price = (updatedItem.quantity || 0) * (updatedItem.unit_laundering_price || 0);
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const getSelectedItem = (itemId) => {
    return availableItems.find(item => item.id === parseInt(itemId));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin')}
            className="btn-secondary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Property Items</h1>
                <p className="text-sm text-gray-600">{property?.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Items
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveEdit}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Summary */}
        <div className="bg-white rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-medium text-gray-900">Property Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium text-gray-900">{property?.address}</p>
            </div>
            {property?.latitude && property?.longitude && (
              <div>
                <p className="text-sm text-gray-600">Coordinates</p>
                <p className="font-medium text-gray-900">{property.latitude}, {property.longitude}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Total Items</p>
              <p className="font-medium text-gray-900">{property?.quote_items?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Property Items
              </h2>
              {isEditing && (
                <button
                  onClick={addItem}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              )}
            </div>
          </div>

          <div className="p-6">
            {isEditing ? (
              // Edit Mode
              <div className="space-y-4">
                {editItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                        <select
                          value={item.item_id}
                          onChange={(e) => updateItem(item.id, 'item_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          <option value="">Select an item</option>
                          {availableItems.map(availableItem => (
                            <option key={availableItem.id} value={availableItem.id}>
                              {availableItem.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Rental</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.unit_rental_price}
                          onChange={(e) => updateItem(item.id, 'unit_rental_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Laundering</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.unit_laundering_price}
                          onChange={(e) => updateItem(item.id, 'unit_laundering_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => removeItem(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove Item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-600">Total Rental</p>
                        <p className="font-medium text-gray-900">${item.total_rental_price?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-gray-600">Total Laundering</p>
                        <p className="font-medium text-gray-900">${item.total_laundering_price?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // View Mode
              <div className="space-y-4">
                {property?.quote_items?.length > 0 ? (
                  property.quote_items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="font-medium text-gray-900">{item.item?.name}</p>
                            <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            ${item.total_rental_price} rental
                          </p>
                          <p className="text-sm text-gray-600">
                            ${item.total_laundering_price} laundering
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">No items for this property</p>
                )}
              </div>
            )}

            {/* Summary */}
            {property?.quote_items?.length > 0 && (
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">Property Total:</span>
                  <span className="font-semibold text-gray-900">
                    ${property.quote_items?.reduce((sum, item) => 
                      sum + parseFloat(item.total_rental_price) + parseFloat(item.total_laundering_price), 0
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyItems; 